import { Modal } from '@/components/atoms/Modal';
import { gitLog, type GitLogEntry } from '@/lib/infra/gitLog';
import { getChannel } from '@/lib/infra/storage';

interface VersionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const buildCommit = typeof __BUILD_COMMIT__ !== 'undefined' ? __BUILD_COMMIT__ : 'dev';
const buildTime = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : '';

const TYPE_COLORS: Record<string, string> = {
  feat: 'bg-emerald-500/20 text-emerald-400',
  fix: 'bg-amber-500/20 text-amber-400',
  refactor: 'bg-blue-500/20 text-blue-400',
  docs: 'bg-purple-500/20 text-purple-400',
  test: 'bg-cyan-500/20 text-cyan-400',
  chore: 'bg-gray-500/20 text-gray-400',
  style: 'bg-pink-500/20 text-pink-400',
  perf: 'bg-orange-500/20 text-orange-400',
};

function getTypeBadgeClass(type: string): string {
  return TYPE_COLORS[type] ?? 'bg-gray-500/20 text-gray-400';
}

function groupByDate(entries: GitLogEntry[]): Map<string, GitLogEntry[]> {
  const map = new Map<string, GitLogEntry[]>();
  for (const entry of entries) {
    const group = map.get(entry.date);
    if (group) {
      group.push(entry);
    } else {
      map.set(entry.date, [entry]);
    }
  }
  return map;
}

function formatBuildTime(iso: string): string {
  if (!iso) return 'Unknown';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

export function VersionModal({ isOpen, onClose }: VersionModalProps) {
  const channel = getChannel();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Version"
      size="xl"
    >
      <div className="space-y-5">
        {/* Build identity card */}
        <div className="bg-muted/50 border-border space-y-2.5 rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm">Commit</span>
            <span className="bg-primary/10 text-primary rounded px-2 py-0.5 font-mono text-sm font-semibold">
              {buildCommit}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm">Built</span>
            <span className="text-sm">{formatBuildTime(buildTime)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm">Channel</span>
            <span
              className={`rounded px-2 py-0.5 text-xs font-semibold ${
                channel === 'stable'
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : 'bg-amber-500/20 text-amber-400'
              }`}
            >
              {channel}
            </span>
          </div>
        </div>

        {/* Deploy history */}
        <div>
          <h3 className="text-muted-foreground mb-3 text-xs font-semibold uppercase tracking-wider">
            Deploy History
          </h3>

          {gitLog.length === 0 ? (
            <p className="text-muted-foreground text-sm">No history available.</p>
          ) : (
            <div className="max-h-[400px] space-y-5 overflow-y-auto pr-1">
              {Array.from(groupByDate(gitLog)).map(([date, entries]) => (
                <div key={date}>
                  <h4 className="text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wider">
                    {date}
                  </h4>
                  <div className="space-y-1.5">
                    {entries.map((entry) => (
                      <div
                        key={entry.hash}
                        className="flex items-start gap-2 text-sm"
                      >
                        <span
                          className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${getTypeBadgeClass(entry.type)}`}
                        >
                          {entry.type}
                        </span>
                        <span className="text-muted-foreground/60 mt-0.5 shrink-0 font-mono text-xs">
                          {entry.hash}
                        </span>
                        <span className="text-foreground/80">{entry.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
